import QRCode from 'qrcode';

export interface QRCodeData {
  type: 'asset' | 'site' | 'floc';
  id: string;
  orgId: string;
  name: string;
  code?: string; // For FLOCs - simple identifier
}

/**
 * Generate a QR code data URL for an asset
 */
export async function generateAssetQRCode(
  orgId: string,
  assetId: string,
  assetName: string
): Promise<string> {
  const data: QRCodeData = {
    type: 'asset',
    id: assetId,
    orgId,
    name: assetName,
  };

  const qrData = JSON.stringify(data);

  try {
    const dataUrl = await QRCode.toDataURL(qrData, {
      width: 400,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    });
    return dataUrl;
  } catch (error) {
    console.error('Failed to generate QR code:', error);
    throw new Error('Failed to generate QR code');
  }
}

/**
 * Generate a QR code data URL for a site
 */
export async function generateSiteQRCode(
  orgId: string,
  siteId: string,
  siteName: string
): Promise<string> {
  const data: QRCodeData = {
    type: 'site',
    id: siteId,
    orgId,
    name: siteName,
  };

  const qrData = JSON.stringify(data);

  try {
    const dataUrl = await QRCode.toDataURL(qrData, {
      width: 400,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    });
    return dataUrl;
  } catch (error) {
    console.error('Failed to generate QR code:', error);
    throw new Error('Failed to generate QR code');
  }
}

/**
 * Generate a QR code data URL for a FLOC
 * Uses "FLOC:" prefix to differentiate from asset or serial number QR codes
 */
export async function generateFlocQRCode(flocId: string): Promise<string> {
  // Add FLOC prefix to differentiate from asset/serial QR codes
  const qrData = `FLOC:${flocId}`;

  try {
    const dataUrl = await QRCode.toDataURL(qrData, {
      width: 400,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    });
    return dataUrl;
  } catch (error) {
    console.error('Failed to generate QR code:', error);
    throw new Error('Failed to generate QR code');
  }
}

/**
 * Parse QR code data
 */
export function parseQRCodeData(qrData: string): QRCodeData | null {
  try {
    const data = JSON.parse(qrData) as QRCodeData;

    // Validate structure
    if (!data.type || !data.id || !data.orgId || !data.name) {
      return null;
    }

    if (data.type !== 'asset' && data.type !== 'site' && data.type !== 'floc') {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Failed to parse QR code data:', error);
    return null;
  }
}

/**
 * Download QR code as PNG image
 */
export async function downloadQRCode(
  dataUrl: string,
  filename: string
): Promise<void> {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = `${filename}-qrcode.png`;
  link.click();
}

/**
 * Generate printable QR code label HTML
 */
export function generatePrintableLabel(
  qrCodeUrl: string,
  title: string,
  subtitle: string
): string {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title} QR Code</title>
        <style>
          @page {
            size: 4in 2in;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 2in;
          }
          .label {
            text-align: center;
            padding: 0.25in;
          }
          .qr-code {
            width: 1.5in;
            height: 1.5in;
            margin: 0 auto;
          }
          .title {
            font-size: 14pt;
            font-weight: bold;
            margin-top: 0.1in;
            margin-bottom: 0.05in;
          }
          .subtitle {
            font-size: 10pt;
            color: #666;
          }
        </style>
      </head>
      <body>
        <div class="label">
          <img src="${qrCodeUrl}" alt="QR Code" class="qr-code" />
          <div class="title">${title}</div>
          <div class="subtitle">${subtitle}</div>
        </div>
      </body>
    </html>
  `;
}

/**
 * Print QR code label
 */
export function printQRCodeLabel(
  qrCodeUrl: string,
  title: string,
  subtitle: string
): void {
  const html = generatePrintableLabel(qrCodeUrl, title, subtitle);

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow popups to print QR code labels');
    return;
  }

  printWindow.document.write(html);
  printWindow.document.close();

  // Wait for image to load before printing
  setTimeout(() => {
    printWindow.print();
  }, 500);
}

/**
 * Generate bulk printable QR code labels (multiple per page)
 */
export function generateBulkPrintableLabels(
  qrCodes: Array<{ url: string; title: string; subtitle: string }>
): string {
  const labelsHtml = qrCodes
    .map(
      ({ url, title, subtitle }) => `
      <div class="label">
        <img src="${url}" alt="QR Code" class="qr-code" />
        <div class="title">${title}</div>
        <div class="subtitle">${subtitle}</div>
      </div>
    `
    )
    .join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>FLOC QR Codes</title>
        <style>
          @page {
            size: letter;
            margin: 0.5in;
          }
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
          }
          .labels-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 0.25in;
          }
          .label {
            text-align: center;
            padding: 0.25in;
            border: 1px solid #ddd;
            page-break-inside: avoid;
          }
          .qr-code {
            width: 1.5in;
            height: 1.5in;
            margin: 0 auto;
          }
          .title {
            font-size: 11pt;
            font-weight: bold;
            margin-top: 0.1in;
            margin-bottom: 0.05in;
            word-wrap: break-word;
          }
          .subtitle {
            font-size: 9pt;
            color: #666;
            word-wrap: break-word;
          }
          @media print {
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="no-print" style="padding: 0.5in; background: #f0f0f0; margin-bottom: 0.25in;">
          <h2>FLOC QR Codes - ${qrCodes.length} labels</h2>
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 14px; cursor: pointer;">
            Print All Labels
          </button>
        </div>
        <div class="labels-grid">
          ${labelsHtml}
        </div>
      </body>
    </html>
  `;
}

/**
 * Print bulk QR code labels
 */
export function printBulkQRCodeLabels(
  qrCodes: Array<{ url: string; title: string; subtitle: string }>
): void {
  const html = generateBulkPrintableLabels(qrCodes);

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow popups to print QR code labels');
    return;
  }

  printWindow.document.write(html);
  printWindow.document.close();
}
